/// Pure pool-pick helper for `randomAircraft` and `timed` scenarios.
/// Mirrors the iOS spawner branch at AircraftSpawner.swift:69-99 —
/// pick a random pool entry, then a random allowed type from that
/// entry's restrictions (falling back to a default mix when the
/// entry is unrestricted).

import type {
  AircraftGoal,
  AircraftType,
  PossibleGoal,
} from '../types/LevelData'
import type { RandomSource } from './rng'

/// Default aircraft-type mix used when a pool entry doesn't restrict
/// types. Mirrors iOS's `AircraftType.allCases` fallback at
/// AircraftSpawner.swift:91-95: with no `allowedTypes` filter the
/// spawner is free to pick any of the six types, so unrestricted
/// pool entries (e.g. KGCN's six entries, LHR's six unrestricted
/// entries) produce a fully mixed bag — heavies, helicopters,
/// military, fighters, plus the regular RJ + light. Levels that
/// want a narrower mix encode it explicitly in `allowedTypes`.
const DEFAULT_TYPES: AircraftType[] = [
  'regionalJet',
  'light',
  'heavy',
  'military',
  'fighter',
  'helicopter',
]

export interface PoolPick {
  goal: AircraftGoal
  type: AircraftType
}

/// Pick one (goal, type) tuple from the given pool, or null if the
/// pool is empty. Pure — no side effects beyond consuming RNG.
export function pickFromPool(
  pool: PossibleGoal[],
  rng: RandomSource,
): PoolPick | null {
  if (pool.length === 0) return null
  const entry = pool[Math.floor(rng() * pool.length)]
  if (!entry) return null
  const allowed =
    entry.allowedTypes && entry.allowedTypes.length > 0
      ? entry.allowedTypes
      : DEFAULT_TYPES
  const type = allowed[Math.floor(rng() * allowed.length)]
  if (!type) return null
  return { goal: entry.goal, type }
}

/// For the LevelBrief readout: estimate the goal-type breakdown from
/// the pool given a target spawn count. Distributes `total` across
/// pool entries proportionally to how often each type appears, then
/// rounds. The HUD doesn't actually need exact counts — this just
/// drives the "expected mix" line on the brief modal.
export function estimatePoolBreakdown(
  pool: PossibleGoal[],
  total: number,
): { landings: number; transitions: number; departures: number } {
  if (pool.length === 0 || total === 0) {
    return { landings: 0, transitions: 0, departures: 0 }
  }
  let landWeight = 0
  let transitionWeight = 0
  let takeoffWeight = 0
  for (const entry of pool) {
    if (entry.goal.land) landWeight += 1
    else if (entry.goal.transition) transitionWeight += 1
    else if (entry.goal.takeoff) takeoffWeight += 1
  }
  const totalWeight = landWeight + transitionWeight + takeoffWeight
  if (totalWeight === 0) {
    return { landings: 0, transitions: 0, departures: 0 }
  }
  // Use proportional rounding so the three numbers add up to `total`.
  // We round down for the first two and let the third absorb the
  // remainder so the sum is exact.
  const landings = Math.round((landWeight * total) / totalWeight)
  const transitions = Math.round((transitionWeight * total) / totalWeight)
  const departures = Math.max(0, total - landings - transitions)
  return { landings, transitions, departures }
}
